import { useState, useEffect, useMemo } from 'react';
import { getValidToken } from '../utils/auth';

const API_URL = process.env.REACT_APP_API_URL || '';

function todayET() {
  return new Date().toLocaleDateString('en-CA', { timeZone: 'America/New_York' });
}

function calcDTE(date) {
  const msPerDay = 86400000;
  return Math.round((new Date(date + 'T00:00:00') - new Date(todayET() + 'T00:00:00')) / msPerDay);
}

// OptionsWidget shows 0 DTE option contracts for `underlying` (e.g. "SPY" or "SPX").
// Connects via SSE for today's data; falls back to REST polling for other dates.
export default function OptionsWidget({ underlying = 'SPY' }) {
  const slug = underlying.toLowerCase(); // "spy" or "spx"

  const [selectedDate, setSelectedDate] = useState(todayET);
  const [contracts, setContracts] = useState(null);
  const [spyPrice, setSpyPrice] = useState(null);
  const [sort, setSort] = useState({ col: 'volume', dir: 'desc' });
  const [connStatus, setConnStatus] = useState('connecting');

  useEffect(() => {
    const isToday = selectedDate === todayET();
    let abortCtrl = null;
    let reconnectTimer = null;
    let pollTimer = null;
    let mounted = true;
    let sseDelay = 1000;

    setContracts(null);
    setConnStatus('connecting');

    const restURL = `${API_URL}/api/${slug}-0dte?date=${selectedDate}`;

    async function pollOnce() {
      try {
        const token = await getValidToken();
        const res = await fetch(restURL, { headers: { Authorization: `Bearer ${token}` } });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        if (mounted) {
          setContracts(data.contracts);
          setSpyPrice(data.spy_price);
          setConnStatus('live');
        }
      } catch {
        if (mounted) setConnStatus('reconnecting');
      }
    }

    function startPoll(intervalMs) {
      pollOnce();
      pollTimer = setInterval(pollOnce, intervalMs);
    }

    async function connectSSE() {
      if (!mounted) return;
      try {
        const token = await getValidToken();
        abortCtrl = new AbortController();
        const res = await fetch(`${API_URL}/sse/${slug}-0dte`, {
          headers: { Authorization: `Bearer ${token}` },
          signal: abortCtrl.signal,
        });
        if (!res.ok) throw new Error(`SSE ${res.status}`);

        const reader = res.body.getReader();
        const dec = new TextDecoder();
        let buf = '';
        if (mounted) { setConnStatus('live'); sseDelay = 1000; }

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;
          buf += dec.decode(value, { stream: true });
          const lines = buf.split('\n');
          buf = lines.pop();
          for (const line of lines) {
            if (line.startsWith('data: ')) {
              try {
                const data = JSON.parse(line.slice(6));
                if (mounted) { setContracts(data.contracts); setSpyPrice(data.spy_price); }
              } catch { /* ignore malformed frame */ }
            }
          }
        }
      } catch (e) {
        if (!mounted || e.name === 'AbortError') return;
        if (mounted) setConnStatus('reconnecting');
        if (sseDelay <= 8000) {
          reconnectTimer = setTimeout(() => { sseDelay = Math.min(sseDelay * 2, 30000); connectSSE(); }, sseDelay);
        } else {
          startPoll(5000);
        }
      }
    }

    if (isToday) {
      connectSSE();
    } else {
      startPoll(30000);
    }

    return () => {
      mounted = false;
      if (abortCtrl) abortCtrl.abort();
      clearTimeout(reconnectTimer);
      clearInterval(pollTimer);
    };
  }, [selectedDate, slug]);

  const toggleSort = (col) =>
    setSort(s => s.col === col ? { col, dir: s.dir === 'desc' ? 'asc' : 'desc' } : { col, dir: 'desc' });

  const sorted = useMemo(() => {
    if (!contracts) return null;
    const key = { volume: 'volume', price: 'price', change_pct: 'change_pct', strike: 'strike' }[sort.col] || 'volume';
    return [...contracts].sort((a, b) =>
      sort.dir === 'desc' ? b[key] - a[key] : a[key] - b[key]
    );
  }, [contracts, sort]);

  const dotColor = connStatus === 'live' ? '#22c55e' : connStatus === 'reconnecting' ? '#f59e0b' : '#6b7280';
  const dotLabel = connStatus === 'live' ? 'Live' : connStatus === 'reconnecting' ? 'Reconnecting…' : 'Connecting…';

  const SortArrow = ({ col }) =>
    sort.col === col
      ? <span className="ml-1" style={{ color: '#60a5fa' }}>{sort.dir === 'desc' ? '↓' : '↑'}</span>
      : <span className="ml-1 text-gray-600">↕</span>;

  return (
    <div className="bg-gray-900 rounded-2xl p-5 mb-5">
      <div className="flex items-center justify-between mb-4">
        {/* Left: date picker + DTE badge */}
        <div className="flex items-center gap-2">
          <input
            type="date"
            value={selectedDate}
            onChange={e => e.target.value && setSelectedDate(e.target.value)}
            className="text-xs rounded px-2 py-1 border focus:outline-none focus:border-blue-500"
            style={{ backgroundColor: '#1f2937', color: '#e5e7eb', borderColor: '#374151' }}
          />
          {(() => {
            const dte = calcDTE(selectedDate);
            const color = dte === 0 ? '#22c55e' : dte < 0 ? '#ef4444' : '#f59e0b';
            return (
              <span className="text-xs font-semibold px-2 py-0.5 rounded" style={{ backgroundColor: color + '22', color }}>
                {dte} DTE
              </span>
            );
          })()}
        </div>

        {/* Center: underlying live price */}
        <div className="flex items-center gap-1.5">
          <span className="text-sm font-bold" style={{ color: '#f9fafb' }}>{underlying}</span>
          {spyPrice === null
            ? <span className="w-16 h-4 bg-gray-800 rounded animate-pulse inline-block align-middle" />
            : spyPrice > 0
              ? <span className="text-sm font-bold tabular-nums" style={{ color: '#60a5fa' }}>${spyPrice.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
              : <span className="text-sm text-gray-500">—</span>}
        </div>

        {/* Right: connection status */}
        <div className="flex items-center gap-2">
          <span className="inline-block w-2 h-2 rounded-full" style={{ backgroundColor: dotColor }} />
          <span className="text-xs text-gray-400">{dotLabel}</span>
        </div>
      </div>

      {sorted === null && (
        <div className="space-y-2">
          {[...Array(6)].map((_, i) => (
            <div key={i} className="h-7 bg-gray-800 rounded animate-pulse" />
          ))}
        </div>
      )}

      {sorted !== null && sorted.length === 0 && (
        <div className="text-center py-8 text-gray-500 text-sm">No 0 DTE contracts found</div>
      )}

      {sorted !== null && sorted.length > 0 && (
        <div className="overflow-auto" style={{ maxHeight: 320 }}>
          <table className="w-full text-sm">
            <thead className="sticky top-0 bg-gray-900">
              <tr>
                <th className="text-left pb-2 pr-3 text-xs font-medium text-gray-400 uppercase tracking-wider w-16">Type</th>
                <th
                  className="text-left pb-2 pr-3 text-xs font-medium text-gray-400 uppercase tracking-wider cursor-pointer select-none hover:text-gray-200"
                  onClick={() => toggleSort('strike')}
                >Contract <SortArrow col="strike" /></th>
                <th
                  className="text-right pb-2 pr-3 text-xs font-medium text-gray-400 uppercase tracking-wider cursor-pointer select-none hover:text-gray-200"
                  onClick={() => toggleSort('price')}
                >Price <SortArrow col="price" /></th>
                <th
                  className="text-right pb-2 pr-3 text-xs font-medium text-gray-400 uppercase tracking-wider cursor-pointer select-none hover:text-gray-200"
                  onClick={() => toggleSort('change_pct')}
                >Chg% <SortArrow col="change_pct" /></th>
                <th
                  className="text-right pb-2 text-xs font-medium text-gray-400 uppercase tracking-wider cursor-pointer select-none hover:text-gray-200"
                  onClick={() => toggleSort('volume')}
                >Volume <SortArrow col="volume" /></th>
              </tr>
            </thead>
            <tbody>
              {sorted.map(c => {
                const isCall = c.type === 'C';
                const chgColor = c.change_pct >= 0 ? '#22c55e' : '#ef4444';
                // Convert Alpaca OCC symbol to TradingView OPRA format.
                // OCC: SPY260625C00740000 → last 9 chars = type(1) + strike(8)
                // OPRA: SPY260625C740.0
                const tvBase = c.symbol.slice(0, -9);
                const tvStrike = Number.isInteger(c.strike) ? c.strike.toFixed(1) : c.strike.toString();
                const tvLink = `https://www.tradingview.com/chart/GYtOhxAt/?symbol=OPRA%3A${tvBase}${c.type}${tvStrike}`;
                return (
                  <tr key={c.symbol} className="border-t border-gray-800 hover:bg-gray-800/40">
                    <td className="py-1.5 pr-3">
                      <span
                        className="px-1.5 py-0.5 rounded text-xs font-semibold"
                        style={{
                          backgroundColor: isCall ? 'rgba(34,197,94,0.15)' : 'rgba(239,68,68,0.15)',
                          color: isCall ? '#22c55e' : '#ef4444',
                        }}
                      >{isCall ? 'CALL' : 'PUT'}</span>
                    </td>
                    <td className="py-1.5 pr-3">
                      <a
                        href={tvLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="font-mono text-xs hover:text-blue-300"
                        style={{ color: '#60a5fa' }}
                      >${c.strike % 1 === 0 ? c.strike.toFixed(0) : c.strike.toFixed(1)}</a>
                    </td>
                    <td className="py-1.5 pr-3 text-right font-mono text-gray-200">${c.price.toFixed(2)}</td>
                    <td className="py-1.5 pr-3 text-right font-mono font-semibold" style={{ color: chgColor }}>
                      {c.change_pct >= 0 ? '+' : ''}{c.change_pct.toFixed(1)}%
                    </td>
                    <td className="py-1.5 text-right font-mono text-gray-300">{c.volume.toLocaleString()}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
